import { useEffect } from 'react';
import { api } from '../services/api.service';
import { shouldSkipApiCalls } from '../utils/routeUtils';

const ThemeInitializer = () => {
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // First, check localStorage for saved theme
        const savedTheme = localStorage.getItem('app-theme');
        const savedColors = localStorage.getItem('app-theme-custom-colors');
        
        if (savedTheme) {
          applyTheme(savedTheme, savedColors ? JSON.parse(savedColors) : undefined);
        }
        
        // Only fetch from backend on private routes to avoid unnecessary API calls
        if (!shouldSkipApiCalls()) {
          // Then try to fetch from backend
          try {
            const settings = await api.settings.general.get(1);
            if (settings?.theme) {
              applyTheme(settings.theme, settings.customColors);
            }
          } catch (error) {
            // Backend fetch failed, use localStorage theme or default
            if (!savedTheme) {
              applyTheme('light');
            }
          }
        } else {
          // On public pages, use localStorage theme or default
          if (!savedTheme) {
            applyTheme('light');
          }
        }
      } catch (error) {
        console.error('Failed to initialize theme:', error);
        // Fall back to light theme
        applyTheme('light');
      }
    };

    const applyTheme = (theme: string, colors?: any) => {
      const root = document.documentElement;
      
      // Remove all theme classes
      root.classList.remove('light', 'dark', 'custom');
      
      // Apply theme-specific styles
      if (theme === 'custom' && colors) {
        root.classList.add('custom');
        // Apply custom CSS variables
        Object.entries(colors).forEach(([key, value]) => {
          root.style.setProperty(`--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value as string);
        });
      } else if (theme === 'system') {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.add(prefersDark ? 'dark' : 'light');
      } else {
        root.classList.add(theme);
      }
      
      // Store in localStorage for persistence
      localStorage.setItem('app-theme', theme);
      if (theme === 'custom' && colors) {
        localStorage.setItem('app-theme-custom-colors', JSON.stringify(colors));
      }
    };

    // Initialize theme immediately
    initializeTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const currentTheme = localStorage.getItem('app-theme');
      if (currentTheme === 'system') {
        applyTheme('system');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return null;
};

export default ThemeInitializer;